/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * RunDetailPage — /results/:eventActivityId/:eventResultId
 *
 * The run behind one result: the route the phone recorded, the kilometre
 * splits, the climb, and the figures underneath them. Public, like the board
 * it opens from — a visitor with the link reads the same page a signed-in
 * athlete does, which is what entering a public race means.
 *
 * A SEPARATE FILE ON PURPOSE. The board at /results/:id works, and the rule on
 * this feature is that a regression costs more than a new screen gains. Adding
 * a few hundred lines to LeaderboardPage would have put this page's failures
 * inside the one that already works; here, the worst this file can do is fail
 * on its own.
 *
 * THREE READS, EACH ALLOWED TO FAIL ALONE.
 *   1. the field for this race  — the board's own read, reused. It is what
 *      gives this page a PLACE and a GAP: a time on its own is not a result.
 *      It also carries the result row itself, because `id` is a primary key
 *      and not a registered field, so the public table endpoint cannot filter
 *      on it — the row has to be found among the field.
 *   2. the run detail — route and summary, from a saved query.
 *   3. the splits — from a saved query. An empty answer is NORMAL: nothing
 *      writes splits for an imported result, and the section simply goes.
 *
 * Each uses `retry: 1`. The default three turned a failing read into nearly
 * ten seconds of apparent loading on the board, which is the single worst bug
 * this feature has had; the same mistake is not repeated here.
 *
 * WHAT IS NOT HONOURED, DELIBERATELY. `activity.privacy` carries the athlete's
 * own PUBLIC / FOLLOWERS / PRIVATE choice, and this page ignores it: the owner
 * settled on 2026-11-03 that a run entered into a public race is published in
 * full, route included. The value is read anyway and shown in the provenance
 * section, so the decision is visible rather than silent — and so honouring it
 * later is a condition on one block, not a re-read.
 */

import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useFeedUser } from '../useFeedData';
// Shared with the board and the index so the app token is minted once. Three
// helpers raced each other for it before this existed and the reads 403'd.
import boardClient from './boardClient';
import { fetchWholeTab, fetchFieldSummary, fetchMyRegistrationIds } from './boardApi';
import {
  fetchRunDetail, fetchRunSplits, parseTrack, routeWithDistance, splitIdOf,
} from './runDetailApi';
import { formatClock, formatKm, formatPace, sortRows } from './boardModel';
import { idOf } from './boardIndexModel';
// The Chrono, so a slow read looks like the board's does rather than like a
// blank page.
import BoardLoader from './BoardLoader';
// The athlete's OWN map and elevation chart, reused rather than redrawn: a run
// should look the same to the public as it does to the person who ran it.
// RouteMap puts the real route on OpenStreetMap tiles; ElevationChart uses the
// same scale the app's chart does, so the same run shows the same shape.
import RouteMap from '../../../components/feed/RouteMap';
import ElevationChart from '../../../components/feed/ElevationChart';
// Both are styled by the feed stylesheet, and both read the feed's CSS custom
// properties. Every rule in it is class-scoped under `fd-`, and the tokens are
// applied as an inline style on a wrapper rather than at :root — so nothing
// here can reach the rest of this public page.
import FEED_TOKENS from '../../../theme/feedTokens';
import '../../../styles/feed.css';

const apiHelper = boardClient;

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
const HAIR = 'rgba(36, 23, 8, .10)';
const CREAM = '#FAF3E7';

const mono = { fontVariantNumeric: 'tabular-nums' };

/** Only the podium needs words; everything else shows the bare number. */
const ORDINALS = { 1: 'First', 2: 'Second', 3: 'Third' };
const card = {
  border: `1px solid ${HAIR}`, borderRadius: 18, background: '#fff', overflow: 'hidden',
};
const label = {
  fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: MUTED,
};
const heading = {
  margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-.01em', color: INK,
};


/** "3:05:00 · 42.20 km" etc. A dash is better than a zero. */
function orDash(value, suffix = '') {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? `${Math.round(n).toLocaleString()}${suffix}` : '—';
}

function Figure({ title, value, note, color = CREAM }) {
  return (
    <div>
      <div style={{ ...label, color: 'rgba(250,243,231,.62)' }}>{title}</div>
      <div style={{ ...mono, marginTop: 5, fontSize: 34, fontWeight: 900, lineHeight: 1, color }}>{value}</div>
      {note ? <div style={{ marginTop: 4, fontSize: 12, color: 'rgba(250,243,231,.62)' }}>{note}</div> : null}
    </div>
  );
}

function Cell({ title, value, note }) {
  return (
    <div style={{ padding: '18px 20px', borderRight: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}` }}>
      <div style={label}>{title}</div>
      <div className="rd-cell-v" style={{ ...mono, marginTop: 6, fontSize: 24, fontWeight: 800, lineHeight: 1, color: INK }}>{value}</div>
      {note ? <div style={{ marginTop: 5, fontSize: 12, color: MUTED }}>{note}</div> : null}
    </div>
  );
}

export default function RunDetailPage() {
  const { eventActivityId, eventResultId } = useParams();
  // ?run=<split id>: one run attached DAY BY DAY on a multi-day event. Its
  // figures are that day's, and a race placing means nothing for it — the
  // field read and the standing are skipped, not shown wrong.
  const [searchParams] = useSearchParams();
  const splitId = splitIdOf(searchParams.get('run'));
  const dayRun = splitId !== null;
  // useFeedUser() returns {userId, name, initials, handle} — there is no
  // `user` property and no `id`. Destructuring `{ user }` left it undefined,
  // so the "this one is yours" badge could never appear for anybody.
  const user = useFeedUser();

  // ── The field. Gives this page the result row, the place and the gap. ────
  const field = useQuery({
    queryKey: ['board', 'field', eventActivityId],
    queryFn: ({ signal }) => fetchWholeTab(apiHelper, {
      eventActivityId, statuses: ['FINISHED'], signal,
    }),
    enabled: !!eventActivityId && !dayRun,
    retry: 1,
  });

  const summary = useQuery({
    queryKey: ['board', 'summary', eventActivityId],
    queryFn: ({ signal }) => fetchFieldSummary(apiHelper, { eventActivityId, signal })
      // Same shape the board uses: this read answers with null rather than
      // reporting isError, so the page falls back to a plain title instead of
      // showing a failure for something cosmetic.
      .catch(() => null),
    enabled: !!eventActivityId,
    retry: 1,
  });

  const detail = useQuery({
    queryKey: ['run', 'detail', eventActivityId, eventResultId, splitId],
    queryFn: ({ signal }) => fetchRunDetail(apiHelper, {
      eventResultId, eventActivityId, splitId, signal,
    }),
    enabled: !!eventActivityId && !!eventResultId,
    retry: 1,
  });

  const splits = useQuery({
    queryKey: ['run', 'splits', eventActivityId, eventResultId, splitId],
    queryFn: ({ signal }) => fetchRunSplits(apiHelper, {
      eventResultId, eventActivityId, splitId, signal,
    }),
    enabled: !!eventActivityId && !!eventResultId,
    retry: 1,
  });

  // ── Where this result sits ──────────────────────────────────────────────
  const standing = useMemo(() => {
    if (dayRun) return null;
    const rows = field.data?.rows || [];
    if (!rows.length) return null;
    // Time ascending is the real ranking for a race with a set distance.
    const ranked = sortRows(rows, 'duration_seconds', 'asc');
    const index = ranked.findIndex((r) => String(r.id) === String(eventResultId));
    if (index < 0) return { row: null, ranked };
    const winner = ranked[0];
    const row = ranked[index];
    const gap = Number(row.duration_seconds) - Number(winner.duration_seconds);
    return {
      row,
      ranked,
      place: index + 1,
      total: ranked.length,
      gapSeconds: Number.isFinite(gap) && gap > 0 ? gap : 0,
      winnerName: winner.athlete_name || 'the winner',
      ahead: ranked[index - 1] || null,
      behind: ranked[index + 1] || null,
    };
  }, [dayRun, field.data, eventResultId]);

  const run = detail.data || null;
  // A day's run: the official figures are the split's, copied at attach time,
  // and the name and bib come from the entrant's public result row.
  const dayRow = useMemo(() => (dayRun && run ? {
    athlete_name: run.athlete_name,
    bib: run.bib,
    registration_id: run.registration_id,
    duration_seconds: run.official_seconds ?? run.duration_seconds,
    distance_meters: run.official_distance_meters ?? run.distance_meters,
  } : null), [dayRun, run]);
  const row = dayRun ? dayRow : (standing?.row || null);

  // ── The route ───────────────────────────────────────────────────────────
  const route = useMemo(() => routeWithDistance(parseTrack(run?.track)), [run]);
  const hasRoute = route.length >= 2;
  const hasAltitude = useMemo(() => route.some((p) => p.alt !== null), [route]);
  /** Highest point reached — the app shows this beside elevation gain. */
  const maxElev = useMemo(() => {
    const alts = route.map((p) => p.alt).filter((a) => a !== null && Number.isFinite(a));
    return alts.length ? Math.max(...alts) : null;
  }, [route]);

  /**
   * Is this the viewer's own run?
   *
   * Asked of the REGISTRATION, not of who created the result row. An
   * organizer-attached result carries the organizer as its creator, so the
   * old test told the athlete this was somebody else's race — see
   * fetchMyRegistrationIds. The read answers null when it cannot be asked at
   * all, and the badge simply does not appear; it never guesses yes.
   */
  const myRegistrations = useQuery({
    queryKey: ['board', 'my-registrations', eventActivityId, user.userId],
    queryFn: ({ signal }) => fetchMyRegistrationIds(apiHelper, {
      userId: user.userId, eventActivityId, signal,
    }),
    enabled: user.userId !== null && !!eventActivityId,
    retry: 1,
  });

  const isMine = !!(
    user.userId !== null && row && (
      // idOf, because a foreign key comes back from the public table read as
      // an EXPANDED object — {related_table_name, record_identifier, data:{id}}
      // — not as a bare id. Comparing it directly compares against
      // "[object Object]", which is never anybody's registration.
      (myRegistrations.data || []).some((id) => String(id) === String(idOf(row.registration_id)))
      // Kept for a run the athlete attached themselves on an environment where
      // the registration read is not permitted.
      || String(row.created_by_id) === String(user.userId)
    )
  );

  const raceName = summary.data?.race_name || 'this race';
  const athlete = row?.athlete_name || run?.title || 'This run';

  // Memoised, not `splits.data || []`: a fresh [] on every render makes every
  // memo below it recompute, which is the opposite of what they are for.
  const splitRows = useMemo(() => splits.data || [], [splits.data]);
  const fastestSplit = useMemo(() => {
    const paces = splitRows
      .map((s) => Number(s.pace_seconds_per_km))
      .filter((n) => Number.isFinite(n) && n > 0);
    return paces.length ? Math.min(...paces) : null;
  }, [splitRows]);
  /** Where the clock stood at each marker — the split times, accumulated. */
  const elapsedAt = useMemo(() => {
    let total = 0;
    return splitRows.map((s) => {
      total += Number(s.duration_seconds) || 0;
      return total;
    });
  }, [splitRows]);

  const slowestSplit = useMemo(() => {
    const paces = splitRows
      .map((s) => Number(s.pace_seconds_per_km))
      .filter((n) => Number.isFinite(n) && n > 0);
    return paces.length ? Math.max(...paces) : null;
  }, [splitRows]);

  /**
   * How many splits are on screen. A marathon has 42 and a reader wants the
   * first few, not a wall — the table was taller than everything else on the
   * page put together. Ten at a time, and the rest on request.
   */
  const SPLITS_STEP = 10;
  const [splitsShown, setSplitsShown] = useState(SPLITS_STEP);

  const loading = (!dayRun && field.isPending) || detail.isPending;
  const gone = !loading && !detail.isError && !run;

  return (
    <div style={{ background: '#FBF7EF', minHeight: '100%', paddingBottom: 72 }}>
      <style>{`
        /* The board's own shimmer, so a slow read looks the same on both
           screens. Two grounds: one for the dark hero, one for the cream body. */
        @keyframes rd-skel-sweep { from { background-position: -240px 0 } to { background-position: 460px 0 } }
        .rd-skel {
          background-color: rgba(250,243,231,.10);
          background-image: linear-gradient(90deg, rgba(250,243,231,0) 0%, rgba(250,243,231,.20) 45%, rgba(250,243,231,0) 70%);
          background-size: 400px 100%; background-repeat: no-repeat;
          animation: rd-skel-sweep 1.3s linear infinite; border-radius: 10px;
        }
        .rd-skel-ink {
          background-color: rgba(36,23,8,.07);
          background-image: linear-gradient(90deg, rgba(36,23,8,0) 0%, rgba(36,23,8,.10) 45%, rgba(36,23,8,0) 70%);
          background-size: 400px 100%; background-repeat: no-repeat;
          animation: rd-skel-sweep 1.3s linear infinite; border-radius: 10px;
        }
        @media (prefers-reduced-motion: reduce) { .rd-skel, .rd-skel-ink { animation: none } }
        @media (max-width: 720px) {
          .rd-wrap { padding: 0 16px !important; }
          .rd-pad { padding: 26px 16px 28px !important; }
          .rd-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          /* ELEVATION STAYS ON A PHONE. It used to be hidden to make room;
             now the table tightens instead — no minimum width, slim padding,
             a bar that shrinks to whatever is left and a short heading over
             it — so all five columns fit a 320px screen without scrolling. */
          .rd-splits { min-width: 0 !important; }
          .rd-splits th, .rd-splits td { padding-left: 6px !important; padding-right: 6px !important; }
          .rd-splits th:first-child, .rd-splits td:first-child { padding-left: 16px !important; width: 28px !important; }
          .rd-splits th:last-child, .rd-splits td:last-child { padding-right: 16px !important; }
          .rd-splits th { letter-spacing: .06em !important; }
          .rd-splits .rd-split-time { font-size: 15px !important; }
          .rd-splits .rd-split-num { font-size: 15px !important; }
          .rd-splits .rd-split-bar-cell { width: 100%; }
          .rd-splits .rd-split-bar-row { gap: 6px !important; flex-wrap: wrap; }
          .rd-splits .rd-split-bar { width: 100% !important; min-width: 40px; max-width: none !important; flex: 1 1 40px !important; }
          .rd-splits .rd-split-note { font-size: 11px !important; }
          .rd-splits td.rd-split-elev, .rd-splits td.rd-split-elapsed { font-size: 13px !important; white-space: nowrap; }
          .rd-th-long { display: none; }
          .rd-th-short { display: inline !important; }
          .rd-charts { flex-direction: column !important; }

          /* Four figures as a 2x2 block, not a flex row. Wrapping put three on
             the first line and one orphan on the second, which read as a
             mistake rather than a layout. */
          .rd-figs {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 18px 14px !important;
          }

          /* The three standing cells were 220px wide at a 358px viewport, so
             each took a whole row and the band ran half a screen tall. Two
             across, with the third spanning the width underneath. */
          .rd-standing { display: grid !important; grid-template-columns: 1fr 1fr !important; }
          .rd-standing > div {
            border-right: 0 !important;
            border-bottom: 1px solid rgba(36,23,8,.10) !important;
            padding: 16px 18px !important;
          }
          .rd-standing > div:nth-child(1) { border-right: 1px solid rgba(36,23,8,.10) !important; }
          .rd-standing > div:nth-child(3) { grid-column: 1 / -1; border-bottom: 0 !important; }

          /* The four route facts, two up — they were one per row, which made
             a four-line column out of four short numbers. */
          .rd-facts { display: grid !important; grid-template-columns: 1fr 1fr !important; }
          .rd-facts > div {
            border-right: 0 !important;
            border-bottom: 1px solid rgba(36,23,8,.10) !important;
            padding: 14px 18px !important;
          }
          .rd-facts > div:nth-child(odd) { border-right: 1px solid rgba(36,23,8,.10) !important; }

          /* "42.20 km" and "2,574 kcal" broke onto a second line in a half
             width column. Smaller, so each figure stays on one. */
          .rd-figs div > div:nth-child(2) { font-size: 27px !important; }
          .rd-cell-v { font-size: 20px !important; }

          /* There is no room for him BESIDE the type at this width, so he
             goes BEHIND it: faint enough that the headline reads cleanly over
             him, present enough that the hero is not a plain brown box. */
          .rd-runner {
            /* Flush to the edge, not past it. At right:-18px his outstretched
               hand fell outside the hero, which clips its overflow — so the
               figure lost the very thing that makes it read as running. */
            right: 0 !important;
            max-height: 280px !important;
            /* .42, the same step back the board gives its pack at this width
               (.mb-hero-pack in LeaderboardPage), so the two pages hold the
               same presence on a phone. Was .22 — set when he was a flat dark
               fill, where any more read as a muddy blob. As a lit gradient he
               needs the board's value or he all but vanishes next to it. */
            /* opacity: .22 !important; */
            opacity: .42 !important;
          }
        }
      `}</style>

      {/* ══ HERO — the board's own ground, so this reads as the same place ══ */}
      <header style={{
        position: 'relative', overflow: 'hidden',
        // The board's radial glow behind the figure, over the same ground the
        // hero always had, so the runner is lit from the page rather than
        // sitting on a flat brown bar. Matches LeaderboardPage's hero glow.
        background: 'radial-gradient(60% 110% at 82% 60%, rgba(255,106,26,.50) 0%, rgba(255,106,26,.12) 45%, rgba(255,106,26,0) 70%), linear-gradient(100deg, #2A1408 0%, #22120A 42%, #150C06 100%)',
        color: CREAM,
      }}
      >

        {/* One runner, not a pack: this page is about one person's race.
            Duotoned into the ground the same way the board treats its photo,
            so he reads as printed into the poster rather than pasted on. */}
        <img
          src="/images/runner-solo.png"
          alt=""
          aria-hidden="true"
          className="rd-runner"
          style={{
            position: 'absolute', right: 40, bottom: 0, height: '100%', maxHeight: 400,
            filter: 'brightness(0) saturate(100%) invert(19%) sepia(58%) saturate(1900%) hue-rotate(2deg) brightness(94%) contrast(96%)',
            opacity: .92, pointerEvents: 'none',
          }}
        />
        */}
        <div
          aria-hidden="true"
          className="rd-runner"
          style={{
            // The mask has no intrinsic size the way an <img> did, so the box
            // carries the art's own shape (418 / 1003) and the mask is drawn
            // to `contain` inside it. Same height and inset as before.
            position: 'absolute', right: 40, bottom: 0, height: '100%', maxHeight: 400,
            aspectRatio: '0.4168',
            opacity: .92, pointerEvents: 'none',
            background: 'linear-gradient(180deg, #FF9A5C 0%, #E2581A 55%, #9A3412 100%)',
            WebkitMask: 'url(/images/runner-solo.png) right bottom / contain no-repeat',
            mask: 'url(/images/runner-solo.png) right bottom / contain no-repeat',
          }}
        />

        {/* Keeps the left side dark under the type, whatever the figure does. */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(94deg, #2A1408 0%, rgba(42,20,8,.94) 34%, rgba(42,20,8,.34) 64%, rgba(42,20,8,.06) 100%)',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, opacity: .45,
            backgroundImage: 'radial-gradient(rgba(250,243,231,.16) 1px, transparent 1.1px)',
            backgroundSize: '6px 6px',
          }}
        />

        <div className="rd-pad" style={{ position: 'relative', maxWidth: 1180, margin: '0 auto', padding: '26px 32px 34px' }}>
          <Link
            to={`/results/${eventActivityId}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44,
              fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase',
              color: 'rgba(250,243,231,.72)', textDecoration: 'none',
            }}
          >
            ← All results{summary.data?.race_name ? ` · ${summary.data.race_name}` : ''}
          </Link>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px',
              borderRadius: 999, background: ACCENT, color: '#fff',
              fontSize: 11, fontWeight: 800, letterSpacing: '.18em', textTransform: 'uppercase',
            }}
            >
              Official result
            </span>
            {dayRun && Number(run?.day_number) > 0 ? (
              <span style={{
                padding: '7px 14px', borderRadius: 999, background: CREAM, color: INK,
                fontSize: 11, fontWeight: 800, letterSpacing: '.18em', textTransform: 'uppercase',
              }}
              >
                {`Day ${Number(run.day_number)}`}
              </span>
            ) : null}
            {isMine ? (
              <span style={{
                padding: '7px 14px', borderRadius: 999, border: `2px solid ${CREAM}`,
                fontSize: 11, fontWeight: 800, letterSpacing: '.18em', textTransform: 'uppercase',
              }}
              >
                This one is yours
              </span>
            ) : null}

            {/* A PODIUM PLATE, for the top three only.
                The enormous ghosted numeral behind the runner is texture — it
                reads as a shape before it reads as a number. This says the
                placing outright, in the same skewed plate the bib uses, and
                only when there is something to celebrate. Fourth place gets
                nothing here; it is already in the figures below. */}
            {standing?.place && standing.place <= 3 ? (
              <span
                style={{
                  ...mono, display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '6px 16px 7px', transform: 'skewX(-9deg)',
                  background: standing.place === 1 ? '#E8A33D' : CREAM,
                  color: '#241708', fontSize: 12, fontWeight: 900,
                  letterSpacing: '.1em', textTransform: 'uppercase',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="9" r="6" />
                  <path d="M8.2 14.3 7 22l5-2.6L17 22l-1.2-7.7" />
                </svg>
                {ORDINALS[standing.place]} place
              </span>
            ) : null}
          </div>

          <h1 style={{
            margin: '18px 0 0', fontSize: 'clamp(32px, 5.6vw, 62px)', fontWeight: 900,
            lineHeight: 1.02, letterSpacing: '-.03em', color: CREAM, minHeight: '1.1em',
          }}
          >
            {/* A shimmer, never a placeholder name. The board learned this the
                hard way: a stand-in title reads as the real answer, and the
                reader never finds out it was a guess. */}
            {loading ? (
              <span className="rd-skel" style={{ display: 'inline-block', width: 'min(420px, 68%)', height: '.78em', verticalAlign: 'middle' }} />
            ) : athlete}
            {row?.bib ? (
              <span style={{
                ...mono, display: 'inline-block', marginLeft: 12, padding: '2px 14px 6px',
                background: ACCENT, color: '#fff', fontSize: '.52em', transform: 'skewX(-9deg)',
              }}
              >
                {row.bib}
              </span>
            ) : null}
          </h1>

          <p style={{ ...mono, margin: '18px 0 0', fontSize: 14, color: 'rgba(250,243,231,.72)' }}>
            {[raceName, run?.activity_type, run?.started_at ? new Date(run.started_at).toLocaleString() : null]
              .filter(Boolean).join(' · ')}
          </p>

          {/* Aligned to the TOP, not the bottom. With flex-end, the two
              figures that carry a note ("per km", "of 4 finishers") were
              pushed up by the height of that note, so their labels and numbers
              sat a line above Time and Distance. Top alignment puts every
              label on one line and every number on the next, and lets the
              notes hang below where they belong. */}
          <div className="rd-figs" style={{ display: 'flex', alignItems: 'flex-start', gap: 34, flexWrap: 'wrap', marginTop: 26 }}>
            {loading ? (
              ['Time', 'Distance', 'Average pace', 'Place'].map((t) => (
                <div key={t}>
                  <div style={{ ...label, color: 'rgba(250,243,231,.62)' }}>{t}</div>
                  <span className="rd-skel" style={{ display: 'block', marginTop: 8, width: 118, height: 30 }} />
                </div>
              ))
            ) : (
              <>
                <Figure title="Time" value={formatClock(row?.duration_seconds)} />
                <Figure title="Distance" value={formatKm(row?.distance_meters)} />
                <Figure
                  title="Average pace"
                  value={formatPace(row?.duration_seconds, row?.distance_meters)}
                  note="per km"
                  color="#F2643D"
                />
                {standing?.place ? (
                  <Figure title="Place" value={`${standing.place}`} note={`of ${standing.total} finishers`} />
                ) : null}
              </>
            )}
          </div>
        </div>
      </header>

      <div className="rd-wrap" style={{ maxWidth: 1180, margin: '0 auto', padding: '0 32px' }}>

        {/* ══ LOADING — the Chrono over a ghost of the real page.
             The board's rule, kept: a shape that matches what is coming, so
             nothing jumps when it lands, and a dial that moves so a slow read
             does not read as a stopped one. ══ */}
        {loading ? (
          <>
            <section aria-label="Loading this run" style={{ ...card, marginTop: 24 }}>
              <BoardLoader label="Retracing the run" note="Fetching the route, the splits and the field." />
              <div style={{ display: 'flex', flexWrap: 'wrap', borderTop: `1px solid ${HAIR}` }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{ flex: '1 1 220px', minWidth: 0, padding: '20px 24px', borderRight: `1px solid ${HAIR}` }}>
                    <span className="rd-skel-ink" style={{ display: 'block', width: 76, height: 9 }} />
                    <span className="rd-skel-ink" style={{ display: 'block', marginTop: 12, width: 132, height: 22 }} />
                    <span className="rd-skel-ink" style={{ display: 'block', marginTop: 10, width: 164, height: 9 }} />
                  </div>
                ))}
              </div>
            </section>

            {/* The map box, at the height the real one will take. */}
            <section aria-hidden="true" style={{ ...card, marginTop: 20 }}>
              <div style={{ padding: '18px 24px 16px' }}>
                <span className="rd-skel-ink" style={{ display: 'block', width: 128, height: 20 }} />
              </div>
              <div className="rd-skel-ink" style={{ height: 460, borderRadius: 0, borderTop: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}` }} />
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} style={{ flex: '1 1 180px', minWidth: 0, padding: '16px 24px', borderRight: `1px solid ${HAIR}` }}>
                    <span className="rd-skel-ink" style={{ display: 'block', width: 62, height: 9 }} />
                    <span className="rd-skel-ink" style={{ display: 'block', marginTop: 10, width: 94, height: 17 }} />
                  </div>
                ))}
              </div>
            </section>

            {/* And the splits, as rows rather than a block, so the page does
                not lurch when forty of them arrive. */}
            <section aria-hidden="true" style={{ ...card, marginTop: 20, padding: '18px 24px 22px' }}>
              <span className="rd-skel-ink" style={{ display: 'block', width: 96, height: 20 }} />
              <div style={{ display: 'grid', gap: 11, marginTop: 18 }}>
                {['86%', '72%', '91%', '64%', '78%'].map((w, i) => (
                  <span key={i} className="rd-skel-ink" style={{ display: 'block', width: w, height: 14 }} />
                ))}
              </div>
            </section>
          </>
        ) : null}

        {/* ══ A read failed, or the result is not on this race ══ */}
        {detail.isError ? (
          <div role="alert" style={{ ...card, marginTop: 24, padding: '22px 24px', background: '#FFF1E8', borderColor: ACCENT }}>
            <p style={{ margin: 0, fontWeight: 700, color: INK }}>This run could not be loaded.</p>
            <p style={{ margin: '6px 0 14px', fontSize: 14, color: MUTED }}>
              The result is still on the board — it is the detail behind it that did not arrive.
            </p>
            <button
              type="button"
              onClick={() => { detail.refetch(); splits.refetch(); }}
              style={{
                minHeight: 44, padding: '0 22px', borderRadius: 999, border: 0,
                background: INK, color: CREAM, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        ) : null}

        {gone ? (
          <div style={{ ...card, marginTop: 24, padding: '22px 24px' }}>
            <p style={{ margin: 0, fontWeight: 700, color: INK }}>There is no run behind this result.</p>
            <p style={{ margin: '6px 0 0', fontSize: 14, color: MUTED }}>
              Either the result belongs to a different race, or it was timed by the
              organizer and carries no recording of its own.
            </p>
          </div>
        ) : null}

        {/* ══ WHERE THIS FINISHED ══ */}
        {standing?.place ? (
          <section aria-label="Where this result sits in the field" style={{ ...card, marginTop: 24 }}>
            <div className="rd-standing" style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: 0, padding: '20px 24px', borderRight: `1px solid ${HAIR}` }}>
                <div style={label}>Overall</div>
                <div style={{ ...mono, marginTop: 6, fontSize: 26, fontWeight: 900, color: INK }}>
                  {standing.place} of {standing.total}
                </div>
                <div style={{ marginTop: 5, fontSize: 12, color: MUTED }}>
                  Finished ahead of {standing.total - standing.place} {standing.total - standing.place === 1 ? 'runner' : 'runners'}
                </div>
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 0, padding: '20px 24px', borderRight: `1px solid ${HAIR}` }}>
                <div style={label}>Behind the winner</div>
                <div style={{ ...mono, marginTop: 6, fontSize: 26, fontWeight: 900, color: standing.gapSeconds ? ACCENT : INK }}>
                  {standing.gapSeconds ? `+${formatClock(standing.gapSeconds)}` : 'Won it'}
                </div>
                <div style={{ marginTop: 5, fontSize: 12, color: MUTED }}>
                  {standing.winnerName}, {formatClock(standing.ranked[0]?.duration_seconds)}
                </div>
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 0, padding: '20px 24px' }}>
                <div style={label}>Timed by</div>
                <div style={{ marginTop: 6, fontSize: 20, fontWeight: 800, color: INK }}>
                  {run ? 'App recording' : 'Organizer'}
                </div>
                <div style={{ marginTop: 5, fontSize: 12, color: MUTED }}>
                  {run ? 'The official figures are a frozen copy' : 'No app recording behind this one'}
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {/* ══ THE ROUTE — the real one, on OpenStreetMap tiles ══ */}
        {hasRoute ? (
          <section aria-label="The route recorded for this run" style={{ ...card, marginTop: 20 }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              gap: 16, flexWrap: 'wrap', padding: '18px 24px 16px',
            }}
            >
              <h2 style={heading}>The route</h2>
              <span style={{ ...mono, fontSize: 12, color: MUTED }}>
                {orDash(run.point_count)} GPS points · recorded in the app
              </span>
            </div>

            {/* The feed's own map component, on the feed's tokens. The wrapper
                carries them as an inline style exactly as FeedLayout does, so
                they reach the map and nothing else on this page. */}
            <div
              style={{ ...FEED_TOKENS, height: 460, borderTop: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}` }}
              className="rd-map"
            >
              <RouteMap
                route={route}
                interactive
                showKm
                attribution
                label={`Route map: ${formatKm(row?.distance_meters)} recorded over ${orDash(run.point_count)} GPS points.`}
              />
            </div>

            <div className="rd-facts" style={{ display: 'flex', flexWrap: 'wrap' }}>
              {[
                ['Measured', formatKm(row?.distance_meters)],
                ['Climb', run.elevation_gain ? `${Math.round(run.elevation_gain)} m` : '—'],
                ['Started', run.started_at ? new Date(run.started_at).toLocaleTimeString() : '—'],
                ['Finished', run.ended_at ? new Date(run.ended_at).toLocaleTimeString() : '—'],
              ].map(([t, v]) => (
                <div key={t} style={{ flex: '1 1 180px', minWidth: 0, padding: '16px 24px', borderRight: `1px solid ${HAIR}` }}>
                  <div style={label}>{t}</div>
                  <div style={{ ...mono, marginTop: 5, fontSize: 19, fontWeight: 800, color: INK }}>{v}</div>
                </div>
              ))}
            </div>

          </section>
        ) : null}

        {run && !hasRoute && !detail.isError ? (
          <section aria-label="The route" style={{ ...card, marginTop: 20, padding: '20px 24px' }}>
            <h2 style={heading}>No route was recorded</h2>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: MUTED }}>
              This run carries a time and a distance but no GPS trace — either the
              phone could not see the sky, or the result was timed rather than tracked.
            </p>
          </section>
        ) : null}

        {/* ══ SPLITS ══ */}
        {splitRows.length > 0 ? (
          <section aria-label="Kilometre splits" style={{ ...card, marginTop: 20 }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              gap: 16, flexWrap: 'wrap', padding: '18px 24px 16px',
            }}
            >
              <h2 style={heading}>Splits</h2>
              <span style={{ ...mono, fontSize: 13, fontWeight: 700, color: ACCENT }}>
                AVG {formatPace(row?.duration_seconds, row?.distance_meters)} / km
              </span>
            </div>

            <div style={{ overflowX: 'auto', borderTop: `1px solid ${HAIR}` }}>
              <table className="rd-splits" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead>
                  <tr>
                    {/* No "Pace" column: for a full-kilometre split the pace
                        IS the split time, and printing 4:23 twice on the same
                        row tells the reader nothing. The bar carries the
                        comparison, and elapsed carries what they actually want
                        to know — where they were on the clock at that marker. */}
                    {[['KM', 'KM'], ['Split', 'Split'], ['Against the rest', 'Vs rest'], ['Elapsed', 'Elapsed'], ['Elevation', 'Elev']].map(([h, short], i) => (
                      <th
                        key={h}
                        scope="col"
                        style={{
                          textAlign: i >= 3 ? 'right' : 'left', padding: '12px 14px',
                          width: i === 0 ? 56 : undefined,
                          fontSize: 11, fontWeight: 600, letterSpacing: '.12em',
                          textTransform: 'uppercase', color: MUTED,
                          borderBottom: `1px solid ${HAIR}`, whiteSpace: 'nowrap',
                        }}
                        className={i === 4 ? 'rd-split-elev' : undefined}
                      >
                        {h === short ? h : (
                          <>
                            <span className="rd-th-long">{h}</span>
                            <span className="rd-th-short" style={{ display: 'none' }}>{short}</span>
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {splitRows.slice(0, splitsShown).map((s, i) => {
                    const pace = Number(s.pace_seconds_per_km);
                    // The last split is usually a part-kilometre, so its pace
                    // and its split time are different numbers and both earn
                    // their place. On a full kilometre they are the same.
                    const partial = Math.abs(Number(s.distance_meters) - 1000) > 1;
                    const isFastest = fastestSplit !== null && pace === fastestSplit;
                    // Fastest kilometre fills the bar; slowest keeps 45% so it
                    // is still a bar and not an empty cell.
                    const span = (slowestSplit || 0) - (fastestSplit || 0);
                    const fill = span > 0 ? 100 - ((pace - fastestSplit) / span) * 55 : 50;
                    const climb = Number(s.elevation_gain);
                    return (
                      <tr key={s.split_number} style={{ background: isFastest ? '#FFF1E8' : '#fff' }}>
                        <td className="rd-split-num" style={{ ...mono, padding: '0 14px', height: 50, borderBottom: `1px solid ${HAIR}`, fontSize: 17, fontWeight: 800, color: MUTED }}>
                          {s.split_number}
                        </td>
                        <td className="rd-split-time" style={{ ...mono, padding: '0 14px', height: 50, borderBottom: `1px solid ${HAIR}`, fontSize: 17, fontWeight: 800, color: isFastest ? ACCENT : INK }}>
                          {formatClock(s.duration_seconds)}
                        </td>
                        <td className="rd-split-bar-cell" style={{ padding: '0 14px', height: 50, borderBottom: `1px solid ${HAIR}` }}>
                          <div className="rd-split-bar-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {/* A RAIL UNDER EVERY BAR, and the bar in the
                                accent rather than grey. Floating grey blocks
                                of varying width are exactly what this page's
                                own skeleton looks like, so a loaded table read
                                as one still loading. A filled track reads as a
                                measure of something; a bare block does not. */}
                            <span className="rd-split-bar" style={{
                              display: 'block', flex: 'none', width: 200, maxWidth: '46vw',
                              height: 10, borderRadius: 6, background: 'rgba(36,23,8,.09)',
                              overflow: 'hidden',
                            }}
                            >
                              <span style={{
                                display: 'block', width: `${Math.max(6, Math.round(fill))}%`,
                                height: '100%', borderRadius: 6,
                                background: isFastest ? ACCENT : 'rgba(194,65,12,.45)',
                              }}
                              />
                            </span>
                            {partial ? (
                              <span className="rd-split-note" style={{ ...mono, fontSize: 13, color: MUTED }}>
                                {formatKm(s.distance_meters)} · {formatPace(s.duration_seconds, s.distance_meters)} /km
                              </span>
                            ) : null}
                            {isFastest ? (
                              <span className="rd-split-note" style={{ ...label, color: ACCENT }}>Fastest</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="rd-split-elapsed" style={{ ...mono, padding: '0 14px', height: 50, textAlign: 'right', borderBottom: `1px solid ${HAIR}`, fontSize: 14, fontWeight: 600, color: INK }}>
                          {formatClock(elapsedAt[i])}
                        </td>
                        <td
                          className="rd-split-elev"
                          style={{ ...mono, padding: '0 14px', height: 50, textAlign: 'right', borderBottom: `1px solid ${HAIR}`, fontSize: 13, color: MUTED }}
                        >
                          {Number.isFinite(climb) ? `+${Math.round(climb)} m` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* More, on request. A count on the button rather than a bare
                "Load more", so the reader knows whether it is three more rows
                or thirty-two before they press it. */}
            {splitsShown < splitRows.length ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 14, flexWrap: 'wrap', padding: '14px 24px 16px', borderTop: `1px solid ${HAIR}`,
              }}
              >
                <span style={{ ...mono, fontSize: 13, color: MUTED }}>
                  Showing {splitsShown} of {splitRows.length} kilometres
                </span>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setSplitsShown((n) => n + SPLITS_STEP)}
                    style={{
                      minHeight: 44, padding: '0 20px', borderRadius: 999,
                      border: `1px solid ${INK}`, background: INK, color: CREAM,
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Show {Math.min(SPLITS_STEP, splitRows.length - splitsShown)} more
                  </button>
                  <button
                    type="button"
                    onClick={() => setSplitsShown(splitRows.length)}
                    style={{
                      minHeight: 44, padding: '0 20px', borderRadius: 999,
                      border: `1px solid ${HAIR}`, background: '#fff', color: INK,
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Show all {splitRows.length}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 14, flexWrap: 'wrap', padding: '14px 24px 16px', borderTop: `1px solid ${HAIR}`,
              }}
              >
                <span style={{ fontSize: 12, color: MUTED, maxWidth: '58ch' }}>
                  One split per full kilometre, plus a final part-kilometre of 200 m
                  or more — so 42.195 km shows 42 splits, not 43.
                </span>
                {splitRows.length > SPLITS_STEP ? (
                  <button
                    type="button"
                    onClick={() => setSplitsShown(SPLITS_STEP)}
                    style={{
                      minHeight: 44, padding: '0 20px', borderRadius: 999,
                      border: `1px solid ${HAIR}`, background: '#fff', color: INK,
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Show fewer
                  </button>
                ) : null}
              </div>
            )}
          </section>
        ) : null}

        {/* ══ ELEVATION — the feed's own chart, so the same run shows the
             same profile here as it does to the athlete in their own app ══ */}
        {hasRoute && hasAltitude ? (
          <section aria-label="Elevation through the run" style={{ ...card, marginTop: 20 }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              gap: 16, flexWrap: 'wrap', padding: '18px 24px 10px',
            }}
            >
              <h2 style={heading}>Elevation</h2>
              <span style={{ ...mono, fontSize: 12, color: MUTED }}>
                {run.elevation_gain ? `${Math.round(run.elevation_gain)} m climbed` : ''}
              </span>
            </div>
            {/* Hovering the chart reads out the kilometre and the height, the
                same as it does in the feed. */}
            <div style={{ ...FEED_TOKENS, padding: '0 14px 14px' }}>
              <ElevationChart route={route} maxElev={null} header={false} />
            </div>
          </section>
        ) : null}

        {/* ══ EVERYTHING THE PHONE RECORDED ══ */}
        {run ? (
          <section aria-label="Everything recorded for this run" style={{ ...card, marginTop: 20 }}>
            <h2 style={{ ...heading, padding: '18px 24px 16px' }}>Performance summary</h2>
            {/* EXACTLY the cells the app's own activity detail shows, in its
                order — `detailStats` in feedModel: distance, moving time,
                average pace, steps, calories, elevation gain, max elevation.
                Nothing extra. A figure here that the athlete cannot find in
                their own app invites the question of where it came from. */}
            <div className="rd-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', borderTop: `1px solid ${HAIR}` }}>
              <Cell title="Distance" value={formatKm(run.distance_meters)} />
              <Cell title="Moving time" value={formatClock(run.duration_seconds)} />
              <Cell
                title="Avg pace"
                value={run.average_pace_seconds_per_km ? `${formatClock(run.average_pace_seconds_per_km)} /km` : '—'}
              />
              <Cell title="Steps" value={orDash(run.total_steps)} />
              <Cell title="Calories" value={run.calories_burned ? `${orDash(run.calories_burned)} kcal` : '—'} />
              <Cell title="Elev gain" value={run.elevation_gain ? `+${Math.round(run.elevation_gain)} m` : '—'} />
              <Cell title="Max elevation" value={maxElev !== null ? `${Math.round(maxElev)} m` : '—'} />
              <Cell title="Started" value={run.started_at ? new Date(run.started_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—'} />
            </div>
            {run.title || run.notes ? (
              <div style={{ padding: '18px 24px 20px' }}>
                <div style={label}>Athlete&rsquo;s notes</div>
                {run.title ? (
                  <p style={{ margin: '8px 0 0', fontSize: 19, fontWeight: 800, color: INK }}>{run.title}</p>
                ) : null}
                {run.notes ? (
                  <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.55, color: '#3B2A14', maxWidth: '62ch' }}>{run.notes}</p>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* ══ WHO CAN SEE THIS — said on the page, not buried in a setting ══ */}
        {run && isMine ? (
          <section aria-label="Who can see this result" style={{ ...card, marginTop: 20, background: '#FFF1E8', padding: '18px 24px' }}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#3B2A14' }}>
              {/* THE VISITOR LINE IS GONE, and the panel with it for anybody
                  but the owner (see the isMine gate on the section above).
                  It read: "This is a public race record. You are seeing what
                  every visitor sees — the run this athlete entered into the
                  race. Nothing else they have recorded is visible here."
                  It explained a privacy boundary to the one person the
                  boundary is not about. The owner's line stays, because it
                  tells somebody a consequence of their own action. */}
              <strong style={{ color: INK }}>Everyone can see this run.</strong>{' '}
              Entering a public race publishes your finish, your route and your
              splits to anyone with the link, signed in or not. Your other runs
              stay private.
            </p>
          </section>
        ) : null}

        {/* ══ MOVE THROUGH THE FIELD ══ */}
        {standing?.place ? (
          <nav aria-label="Other athletes in this race" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 20 }}>
            {[['One place ahead', standing.ahead], ['One place behind', standing.behind]]
              .filter(([, r]) => !!r)
              .map(([dir, r]) => (
                <Link
                  key={dir}
                  to={`/results/${eventActivityId}/${r.id}`}
                  style={{ ...card, flex: '1 1 280px', minWidth: 0, display: 'block', padding: '16px 20px', textDecoration: 'none' }}
                >
                  <div style={label}>{dir}</div>
                  <div style={{ marginTop: 6, fontSize: 19, fontWeight: 800, color: INK }}>
                    {r.athlete_name || 'Athlete'}
                  </div>
                  <div style={{ ...mono, marginTop: 3, fontSize: 13, color: MUTED }}>
                    {formatClock(r.duration_seconds)} · {formatKm(r.distance_meters)}
                  </div>
                </Link>
              ))}
          </nav>
        ) : null}

        <div style={{ marginTop: 22, textAlign: 'center' }}>
          <Link
            to={`/results/${eventActivityId}`}
            style={{
              display: 'inline-block', minHeight: 44, padding: '14px 30px', borderRadius: 999,
              background: INK, color: CREAM, fontWeight: 800, letterSpacing: '.04em',
              textTransform: 'uppercase', fontSize: 14, textDecoration: 'none',
            }}
          >
            Back to the full field
          </Link>
        </div>
      </div>
    </div>
  );
}
